import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage, toastError, toastSuccess } from '../../lib/toast';
import {
  getSpecialities, listAllVendors, listOf, MAX_ROWS, tagVendor,
} from './vendorsApi';
import { VENDOR_ERRORS } from './vendorLabels';
import { Skeleton, FullError } from './VendorBits';

function Row({ vendor, specialities, onRemoved }) {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setPicked((cur) => (cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id]));

  const save = async () => {
    if (busy || !picked.length) return;
    setBusy(true);
    try {
      await tagVendor(vendor.id, picked);
      toastSuccess('Tagged.');
      onRemoved(vendor.id);
    } catch (err) {
      toastError(errorMessage(err, 'Could not tag the vendor.', VENDOR_ERRORS));
      // Someone else tagged it first: this row is stale, so drop it.
      if (err?.response?.data?.error === 'already_tagged') onRemoved(vendor.id);
    } finally {
      setBusy(false);
    }
  };

  // One row per vendor, not a card: triage is a long, repetitive pass, so the eye
  // should run down a column of names with the choices beside each.
  return (
    <li className="vd-triage-row">
      <Link className="vd-triage-name" to={`/vendors/${vendor.id}`} dir="auto">{vendor.name}</Link>
      <div className="vd-filter-chips" role="group" aria-label={`Specialities for ${vendor.name}`}>
        {specialities.map((s) => (
          <button key={s.id} type="button" className="vd-toggle-chip vd-toggle-chip--sm" aria-pressed={picked.includes(s.id)} onClick={() => toggle(s.id)} disabled={busy}>
            {s.name}
          </button>
        ))}
      </div>
      <button type="button" className="btn btn-primary btn-small vd-triage-save" onClick={save} disabled={busy || !picked.length} aria-busy={busy || undefined}>Save</button>
    </li>
  );
}

/** Triage: give untagged vendors their first specialities (written straight to Odoo). */
export default function UntaggedQueue({ onChanged }) {
  const [state, setState] = useState({ status: 'loading', rows: [], specialities: [], capped: false });

  const load = useCallback(() => {
    setState((s) => ({ ...s, status: 'loading' }));
    Promise.all([listAllVendors({ untagged: 1 }), getSpecialities()])
      .then(([vendors, specs]) => {
        setState({ status: 'ready', rows: vendors.rows, capped: vendors.capped, specialities: listOf(specs, 'specialities') });
      })
      .catch(() => setState({ status: 'error', rows: [], specialities: [], capped: false }));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = (id) => {
    setState((s) => ({ ...s, rows: s.rows.filter((r) => r.id !== id) }));
    if (onChanged) onChanged();
  };

  if (state.status === 'loading') return <Skeleton label="Loading untagged vendors" />;
  if (state.status === 'error') return <FullError message="Couldn't load untagged vendors. Try again in a minute." onRetry={load} />;
  if (state.rows.length === 0) return <div className="vd-empty"><p>No untagged vendors.</p></div>;
  // Rows without a single speciality to pick would be 400 dead Save buttons.
  if (state.specialities.length === 0) {
    return (
      <div className="vd-empty">
        <p>No specialities to tag with yet.</p>
        <p className="vd-list-meta">An admin turns Odoo tags into specialities under Admin → Vendors. Untagged vendors show here once there is one to pick.</p>
      </div>
    );
  }
  return (
    <>
      <p className="vd-section-lede">
        Pick each vendor&apos;s first specialities. They save straight to Odoo; later changes go through approval.
        {state.capped && ` Showing the first ${MAX_ROWS}.`}
      </p>
      <ul className="vd-list vd-triage">
        {state.rows.map((v) => <Row key={v.id} vendor={v} specialities={state.specialities} onRemoved={remove} />)}
      </ul>
    </>
  );
}
